import { useState } from 'react';
import { getServerNameCode } from '../../config/gcs/gcsConfigs';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { en } from '../../locales/en';
import { useGcsStore } from '../../store/useGcsStore';
import type { MissionId } from '../../types/gcs/mission';
import type { VehicleId } from '../../types/gcs/vehicle';
import { AsyncAction } from '../../types/general/asyncAction';
import { getShortVehicleId } from '../../utils/formatters';
import { Text } from '../general/Text';
import Dialog from './Dialog';

interface ConfirmTakeoffFromDockDialogProps {
  gcsId: string;
  vehicleId: VehicleId;
  missionId: MissionId;
  onClose: () => void;
}

function ConfirmTakeoffFromDockDialog({ gcsId, vehicleId, missionId, onClose }: ConfirmTakeoffFromDockDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [takeoffAction, setTakeoffAction] = useState<AsyncAction>(AsyncAction.initial());
  const nameCode = getServerNameCode(gcsId);
  const vehicleLabel = getShortVehicleId(vehicleId);

  const dockPointName = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.liveMissionsMap.get(vehicleId)?.dockPoint?.name ?? en.common.notAvailable;
  });

  const onTakeoffClicked = async () => {
    try {
      setTakeoffAction(AsyncAction.loading());
      await useGcsStore.getState().approveTakeoffFromDock(gcsId, missionId);
      setTakeoffAction(AsyncAction.success(undefined));
      onClose();
    } catch (error) {
      setTakeoffAction(AsyncAction.failure(error as Error));
    }
  };

  return (
    <Dialog
      title={en.confirmTakeoffFromDockDialog.title}
      onClose={onClose}
      primaryButtonProps={{
        text: en.confirmTakeoffFromDockDialog.confirmTakeoffButton,
        callback: onTakeoffClicked,
        isLoading: takeoffAction.isLoading,
        backgroundColor: 'var(--color-success)',
        foregroundColor: '#ffffff'
      }}
      errorText={takeoffAction.isFailure ? getLocalizedErrorMessage(takeoffAction.error) : undefined}
    >
      <div style={{ width: 'var(--dialog-common-width)' }}>
        <Text variant='body1'>
          {en.confirmTakeoffFromDockDialog.body
            .replace('{vehicle}', vehicleLabel)
            .replace('{dockPointName}', dockPointName)
            .replace('{nameCode}', nameCode)}
        </Text>
      </div>
    </Dialog>
  );
}

export default ConfirmTakeoffFromDockDialog;
